import { api } from '../api'

interface PushSubscriptionJson {
  endpoint: string
  expirationTime: number | null
  keys?: {
    p256dh: string
    auth: string
  }
}

function supportsWebPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const normalized = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const raw = atob(normalized)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let index = 0; index < raw.length; index += 1) {
    bytes[index] = raw.charCodeAt(index)
  }
  return bytes
}

async function fetchPushPublicKey(): Promise<string | null> {
  const response = await api.get<{ publicKey?: string }>(
    '/notifications/push-public-key',
    { skipAuth: true },
  )
  const key = response.publicKey?.trim()
  return key ? key : null
}

const DEVICE_STORAGE_KEY = 'kuniri.push.device.v1'

/**
 * Stable per-browser identifier. The backend replaces the previous endpoint
 * of the same device when the push service rotates it, so one event is never
 * delivered twice to the same browser (D-080).
 */
export function pushDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_STORAGE_KEY)
    if (existing) return existing
    const generated =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    localStorage.setItem(DEVICE_STORAGE_KEY, generated)
    return generated
  } catch {
    return 'unknown-device'
  }
}

async function registerSubscription(
  subscription: PushSubscriptionJson,
): Promise<void> {
  await api.post<{ ok: boolean }>('/notifications/push-subscriptions', {
    subscription,
    deviceId: pushDeviceId(),
  })
}

async function removeSubscription(endpoint: string): Promise<void> {
  await api.post<{ ok: boolean }>(
    '/notifications/push-subscriptions/remove',
    { endpoint },
  )
}

let migrationPromise: Promise<boolean> | null = null

/**
 * Reproduces the f472f9a web-push worker migration:
 *
 * 1. detect a live subscription under the legacy `/push/` scope;
 * 2. register the new root-scope `/push-sw.js` and subscribe it;
 * 3. register the new subscription with the API;
 * 4. only after that succeeds, remove the old endpoint, unsubscribe it and
 *    unregister the legacy worker.
 *
 * Failure is silent to the user and leaves the old worker in place. Feature
 * code calls this after login; it is safe to call repeatedly.
 */
export function migrateLegacyPushWorker(): Promise<boolean> {
  if (migrationPromise) return migrationPromise
  const migration = runLegacyMigration().finally(() => {
    migrationPromise = null
  })
  migrationPromise = migration
  return migration
}

async function runLegacyMigration(): Promise<boolean> {
  if (!supportsWebPush() || Notification.permission !== 'granted') {
    return false
  }

  try {
    const legacyRegistration =
      await navigator.serviceWorker.getRegistration('/push/')
    if (!legacyRegistration) return false
    const legacySubscription =
      await legacyRegistration.pushManager.getSubscription()
    if (!legacySubscription) return false

    const publicKey = await fetchPushPublicKey()
    if (!publicKey) return false

    const registration = await navigator.serviceWorker.register('/push-sw.js', {
      scope: '/',
    })
    const existing = await registration.pushManager.getSubscription()
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }))

    await registerSubscription(
      subscription.toJSON() as unknown as PushSubscriptionJson,
    )

    if (legacySubscription.endpoint !== subscription.endpoint) {
      await removeSubscription(legacySubscription.endpoint)
      await legacySubscription.unsubscribe()
      await legacyRegistration.unregister()
    }

    return true
  } catch {
    return false
  }
}
